import { styled, SxProps, Theme } from "@mui/material/styles";
import { Switch, SwitchProps } from "@mui/material";
import { theme } from "../theme";

/**
 * Login Page Styles
 *
 * Contains all styling definitions for the Login component.
 * Styles are organized by component hierarchy and usage.
 */
export const createLoginStyles = (theme: Theme) => ({
  // ==============================================
  // LAYOUT & CONTAINER STYLES
  // ==============================================

  /** Main container positioning and sizing - wider than tall design */
  container: {
    position: "relative",
    zIndex: 10,
    width: { xs: "95%", lg: "700px" },
    height: { xs: "50%", lg: "50%" },
    mt: { xs: "100px", lg: 0 },
    ml: { xs: "2.5%", lg: "49%" }, // Aligns with the original Commons design
  },

  /** Paper wrapper with elevation and padding */
  paper: {
    p: 5,
    borderRadius: 0.7,
    bgcolor: "white",
    boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)",
  },

  // ==============================================
  // HEADER & TEXT STYLES
  // ==============================================

  /** Main "Get Started" header styling */
  header: {
    fontFamily: theme.typography.fontFamily,
    fontWeight: "bold",
    color: theme.palette.primary.main,
    opacity: 0.8,
    mb: 1,
  },

  /** Subtitle text with register link */
  subtitle: {
    fontFamily: theme.typography.fontFamily,
    mb: 2,
    color: "#bbb",
    fontWeight: "bold",
  },

  /** Register link styling with seaweed color and hover effect */
  registerLink: {
    color: theme.palette.materiom.seaweed,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },

  // ==============================================
  // FORM INPUT STYLES
  // ==============================================

  /** Error alert spacing */
  errorAlert: {
    mb: 2,
  },

  /** Container for each input field group */
  inputContainer: {
    mb: 2,
  },

  /** Field labels (Email, Password) - close to inputs */
  fieldLabel: {
    color: theme.palette.text.primary,
    mb: 0.3, // Tight spacing to text field
    display: "block",
  },

  /** Text field styling with compact height and seaweed focus color */
  textField: {
    "& .MuiOutlinedInput-root": {
      mt: 0.5,
      height: "45px", // Compact height
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.materiom.seaweed, // Seaweed focus border
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "8px 12px", // Reduced padding for compact look
    },
  },

  // ==============================================
  // CHECKBOX & REMEMBER ME STYLES
  // ==============================================

  /** Container for checkbox and label alignment */
  checkboxContainer: {
    mb: 2,
    display: "flex",
    alignItems: "center",
  },

  /** Checkbox with charcoal border and seaweed checked state */
  checkbox: {
    color: theme.palette.materiom.charcoal, // Unchecked state
    "&.Mui-checked": {
      color: theme.palette.materiom.seaweed, // Checked state
    },
    p: 0,
    borderColor: theme.palette.materiom.charcoal,
    borderWidth: 0.5,
    mr: 1,
  },

  /** Remember me label - clickable and styled */
  rememberMeLabel: {
    fontWeight: "bold",
    color: theme.palette.text.secondary,
    cursor: "pointer", // Indicates clickability
  },

  // ==============================================
  // BUTTON & LINK STYLES
  // ==============================================

  /** Login button - compact height with hover effects */
  loginButton: {
    mt: 1, // Reduced spacing from checkbox
    py: 0.5,
    height: "36px", // Compact height
    borderRadius: "5px",
    bgcolor: theme.palette.primary.main,
    opacity: 0.9,
    color: "white",
    fontWeight: theme.typography.fontWeightBold,
    "&:hover": {
      bgcolor: theme.palette.primary.main,
      opacity: 1, // Full opacity on hover
    },
  },

  /** Container for forgot password link - centered */
  forgotPasswordContainer: {
    textAlign: "center",
    mt: 2,
  },

  /** Forgot password link with hover effects */
  forgotPasswordLink: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    textDecoration: "underline",
    cursor: "pointer",
    "&:hover": {
      color: theme.palette.text.primary, // Darker on hover
    },
  },
});

/**
 * Survey Page Styles
 *
 * Contains all styling definitions for the OnRegistrationSurvey component.
 * Includes autocomplete styling with ripple effect disabled.
 */
export const createSurveyStyles = (theme: Theme) => ({
  // ==============================================
  // AUTOCOMPLETE STYLES
  // ==============================================

  /** Multi-select autocomplete with expandable height */
  multiSelectAutocomplete: {
    "& .MuiOutlinedInput-root": {
      height: "auto",
      minHeight: "45px",
      alignItems: "flex-start",
      paddingTop: "3px",
      paddingBottom: "3px",
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.materiom.seaweed,
      },
    },
    "& .MuiTouchRipple-root": {
      display: "none !important",
    },
  },

  /** Single-select autocomplete with standard height */
  singleSelectAutocomplete: {
    "& .MuiTouchRipple-root": {
      display: "none !important",
    },
  },

  /** Autocomplete dropdown popper styling */
  autocompletePopper: {
    "& .MuiAutocomplete-option": {
      "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.04) !important",
      },
    },
    "& .MuiTouchRipple-root": {
      display: "none !important",
    },
  },
});

/**
 * Dashboard Page Styles
 *
 * Contains all styling definitions for the Dashboard component.
 * Styles are organized by component hierarchy and usage.
 */
export const createDashboardStyles = (theme: Theme) => ({
  // ==============================================
  // LAYOUT & CONTAINER STYLES
  // ==============================================

  /** Main container with responsive padding */
  container: {
    py: 4,
  },

  /** Top margin for desktop/mobile */
  topSection: {
    mt: { lg: 0, xs: 0 },
  },

  /** Content grid container */
  contentGrid: {
    mt: { xs: 3, lg: 3 },
    flexGrow: 1,
  },

  /** Unified grid layout for navigation cards and featured content */
  unifiedGrid: {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr 1fr" },
    gridTemplateRows: { lg: "1fr 1fr" },
    gap: 3,
  },

  // ==============================================
  // INTRODUCTION BAR STYLES
  // ==============================================

  /** Main introduction card - dark header with user info */
  introCard: {
    bgcolor: theme.palette.primary.main,
    color: "white",
    borderRadius: 0.5,
    position: "relative",
    mb: 2,
    padding: { xs: 2, lg: 2 },
  },

  /** Support icon button positioning and responsive sizing */
  supportIconButton: {
    position: "absolute",
    top: "50%",
    right: { lg: 24, xs: 16 },
    transform: "translateY(-50%)",
    width: { lg: 56, xs: 40 },
    height: { lg: 56, xs: 40 },
    bgcolor: "white",
    "&:hover": { bgcolor: "#f5f5f5" },
  },

  /** Support icon styling with responsive sizing */
  supportIcon: {
    color: theme.palette.primary.main,
    fontSize: { xs: 24, lg: 34 },
  },

  /** Support icon button positioning and responsive sizing */
  feedbackIconButton: {
    position: "absolute",
    top: "50%",
    right: { lg: 90, xs: 16 },
    transform: "translateY(-50%)",
    width: { lg: 56, xs: 40 },
    height: { lg: 56, xs: 40 },
    bgcolor: "white",
    "&:hover": { bgcolor: "#f5f5f5" },
  },

  /** Support icon styling with responsive sizing */
  feedbackIcon: {
    color: theme.palette.primary.main,
    position: "relative",
    fontSize: { xs: 24, lg: 34 },
    top: "2px",
    left: "2px",
  },

  /** User info container */
  userInfoContainer: {
    display: "flex",
    alignItems: "center",
  },

  /** User avatar container */
  userAvatarContainer: {
    position: "relative",
    width: { xs: 40, lg: 60 },
    height: { xs: 40, lg: 60 },
  },

  /** User placeholder icon */
  userPlaceholderIcon: {
    width: { xs: 40, lg: 60 },
    height: { xs: 40, lg: 60 },
    bgcolor: "#F5F5F5",
  },

  /** User text container */
  userTextContainer: {
    ml: { xs: 1, lg: 3 },
  },

  /** Dashboard label text */
  dashboardLabel: {
    color: "white",
    fontWeight: "bold",
    fontSize: "0.875rem",
  },

  /** Welcome message */
  welcomeMessage: {
    color: "white",
    fontWeight: "bold",
    fontSize: { xs: "1.25rem", lg: "1.875rem" },
  },

  // ==============================================
  // NAVIGATION CARD STYLES
  // ==============================================

  /** Base navigation card styling */
  navigationCard: {
    textDecoration: "none",
    textAlign: "center",
    p: 3,
    py: 5,
    position: "relative",
    borderRadius: 2,
    boxShadow: "0 0 10px rgba(7, 37, 49, 0.3)",
    border: "1px solid transparent",
    transition: "all 0.3s ease",
    "&:hover .MuiButton-root": {
      bgcolor: theme.palette.primary.main,
      opacity: 1,
    },
  },

  /** Materiom AI card specific styling */
  materiomAICard: {
    boxShadow: "0 0 10px #072531",
    border: "1px solid transparent",
    transition: "all 0.3s cubic-bezier(.4,0,.2,1)",
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
  },

  /** Standard navigation card hover */
  standardCardHover: {
    "&:hover": {
      borderColor: theme.palette.materiom.seaweed,
    },
  },

  /** User Directory card with responsive order */
  userDirectoryCard: {
    order: { xs: 3, lg: 4 },
  },

  /** Contribution Portal card with responsive order */
  contributionCard: {
    order: { xs: 4, lg: 5 },
  },

  // ==============================================
  // ICON & TEXT STYLES
  // ==============================================

  /** Navigation card icons */
  cardIcon: {
    fontSize: 48,
    mb: 2,
  },

  /** Robot icon color for Materiom AI */
  robotIcon: {
    color: theme.palette.materiom.seaweed,
  },

  /** Computer analysis icon color */
  computerAnalysisIcon: {
    color: theme.palette.materiom.seaweed,
  },

  /** Connect and web form icon color */
  standardIcon: {
    color: theme.palette.materiom.seaweed,
  },

  /** Card subtitle text */
  cardSubtitle: {
    color: "text.secondary",
    opacity: 0.7,
    fontWeight: "medium",
    fontSize: "0.875rem",
    mb: 1,
  },

  /** Card title text */
  cardTitle: {
    color: "text.primary",
    fontWeight: "bold",
    mb: 3,
  },

  // ==============================================
  // BUTTON STYLES
  // ==============================================

  /** CTA buttons in navigation cards */
  ctaButton: {
    bgcolor: theme.palette.primary.main,
    opacity: 0.9,
    borderRadius: "10px",
    fontWeight: "bold",
    px: 4,
    boxShadow: "none",
    "&:hover": {
      bgcolor: theme.palette.primary.main,
      opacity: 1,
      boxShadow: "none",
    },
  },

  // ==============================================
  // FEATURED CONTENT STYLES
  // ==============================================

  /** Featured recipes card */
  featuredRecipesCard: {
    bgcolor: "#E9E9E9",
    borderRadius: 2,
    overflow: "hidden",
    order: { xs: 5, lg: 3 },
    boxShadow: "0 0 10px rgba(7, 37, 49, 0.3)",
  },

  /** Featured recipes placeholder content */
  featuredRecipesContent: {
    fontFamily: theme.typography.fontFamily,
    height: 250,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.materiom.charcoal,
    opacity: 0.7,
  },
});

// =============================================================================
// USER DIRECTORY PAGE STYLES
// =============================================================================

/**
 * User Directory Page Styles
 *
 * Contains all styling definitions for the UserDirectory page and its components.
 * Includes both page-level styles and component styles that were previously
 * distributed across multiple files.
 */

// =============================================================================
// STYLED COMPONENTS
// =============================================================================

/**
 * Custom iOS-style Switch component
 * Styled to match iOS toggle design with smooth animations and clean appearance
 */
export const CustomSwitch = styled(Switch)<SwitchProps>(({ theme }) => ({
  width: 42,
  height: 20,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(22px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: theme.palette.primary.light,
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: theme.palette.secondary.main,
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color: theme.palette.grey[100],
      ...theme.applyStyles("dark", {
        color: theme.palette.grey[600],
      }),
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: 0.7,
      ...theme.applyStyles("dark", {
        opacity: 0.3,
      }),
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 16,
    height: 16,
    boxShadow: "none",
  },
  "& .MuiSwitch-track": {
    borderRadius: 20 / 2,
    backgroundColor: "#E9E9EA",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
    ...theme.applyStyles("dark", {
      backgroundColor: "#39393D",
    }),
  },
}));

// =============================================================================
// USER DIRECTORY PAGE LAYOUT STYLES
// =============================================================================

export const userDirectoryStyles = {
  /** Main container for user directory page */
  container: {
    py: 4,
    px: "30px",
  } as SxProps<Theme>,

  /** Paper components for directory sections */
  paper: {
    elevation: 0,
    borderRadius: "10px",
    overflow: "hidden",
  },

  // Filters panel styles
  /** Filters header section with padding */
  filtersHeader: {
    pt: "20px",
    pb: { xs: "20px", lg: 0 },
    px: "20px",
  } as SxProps<Theme>,

  /** Filters header content row */
  filtersHeaderContent: {
    display: "flex",
    alignItems: "center",
    gap: 2,
  } as SxProps<Theme>,

  /** Filters icon styling */
  filtersIcon: {
    color: theme.palette.materiom.seaweed,
    fontSize: 26,
  } as SxProps<Theme>,

  /** Filters header row layout */
  filtersHeaderRow: {
    flex: 1,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  } as SxProps<Theme>,

  /** Filters title text */
  filtersTitle: {
    color: theme.palette.materiom.seaweed,
    fontWeight: 600,
    fontSize: "26px",
  } as SxProps<Theme>,

  /** Filters toggle text styling */
  filtersToggleText: {
    color: "text.secondary",
    fontWeight: 600,
  } as SxProps<Theme>,

  /** Filters content section padding */
  filtersContent: {
    p: "20px",
  } as SxProps<Theme>,

  /** Keyword search section margin */
  keywordSearchSection: {
    mb: 3,
  } as SxProps<Theme>,

  /** Keyword search title styling */
  keywordSearchTitle: {
    mb: 2,
    fontWeight: 600,
    fontSize: "20px",
    color: "text.primary",
  } as SxProps<Theme>,

  /** Keyword search box input styling */
  keywordSearchBox: {
    "& .MuiInputBase-input": {
      fontSize: "0.75rem",
    },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.light,
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.light,
    },
  } as SxProps<Theme>,

  // Results panel styles
  /** Results header section padding */
  resultsHeader: {
    px: "20px",
    pt: "20px",
    pb: "20px",
  } as SxProps<Theme>,

  /** Results title text styling */
  resultsTitle: {
    color: theme.palette.materiom.seaweed,
    fontWeight: 600,
  } as SxProps<Theme>,

  /** Results content styling for Algolia hits */
  resultsContent: {
    px: "20px",
    pt: 0,
    pb: 0,
    "& .ais-Hits-list": {
      listStyle: "none",
      margin: 0,
      padding: 0,
    },
    "& .ais-Hits-item": {
      listStyleType: "none",
    },
  } as SxProps<Theme>,

  /** Pagination container padding */
  paginationContainer: {
    p: "20px",
  } as SxProps<Theme>,
};

// =============================================================================
// DIRECTORY FILTERS COMPONENT STYLES
// =============================================================================

export const directoryFiltersStyles = {
  /** Container for directory filters */
  container: {
    mt: 0,
  } as SxProps<Theme>,

  /** Filter group section margin */
  filterGroup: {
    mb: 3,
  } as SxProps<Theme>,

  /** Filter group title styling */
  filterGroupTitle: {
    mb: 2,
    fontWeight: 600,
    fontSize: "20px",
    color: "text.primary",
  } as SxProps<Theme>,

  /** Toggle container for switches */
  toggleContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    py: 1,
  } as SxProps<Theme>,
};

// =============================================================================
// SEARCH RESULT COMPONENT STYLES
// =============================================================================

export const searchResultStyles = {
  /** Container for each search result row */
  container: {
    py: 2,
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  } as SxProps<Theme>,

  /** Content row layout for search result */
  contentRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
  } as SxProps<Theme>,

  /** User info section in search result */
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    flex: 1,
  } as SxProps<Theme>,

  /** User avatar styling */
  avatar: {
    width: 48,
    height: 48,
  } as SxProps<Theme>,

  /** User details section */
  userDetails: {
    flex: 1,
  } as SxProps<Theme>,

  /** User name text styling */
  userName: {
    mb: 0.5,
    fontWeight: 600,
    fontSize: "1.1rem",
  } as SxProps<Theme>,

  /** User institute text styling */
  userInstitute: {
    mt: 0.5,
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    fontSize: "0.875rem",
  } as SxProps<Theme>,

  /** View button styling for search result */
  viewButton: (theme: Theme) =>
    ({
      backgroundColor: theme.palette.primary.main,
      boxShadow: "none",
      opacity: 0.8,
      color: "white",
      textTransform: "none",
      fontWeight: 600,
      px: 2,
      py: 0.5,
      borderRadius: 1,
      "&:hover": {
        backgroundColor: theme.palette.primary.main,
        opacity: 1,
        boxShadow: "none",
      },
    } as SxProps<Theme>),
};

// =============================================================================
// CUSTOM PAGINATION COMPONENT STYLES
// =============================================================================

export const customPaginationStyles = {
  /** Container for custom pagination */
  container: {
    spacing: 2,
    alignItems: "center",
  },

  /** Pagination styling for Algolia results */
  pagination: (theme: Theme) =>
    ({
      // Disable ripple on all pagination items
      "& .MuiPaginationItem-root": {
        color: theme.palette.primary.main,
        "&.MuiButtonBase-root": {
          WebkitTapHighlightColor: "transparent",
          transition: "background-color 0.2s",
        },
        "&.MuiButtonBase-root:active": {
          boxShadow: "none",
        },
        "&.MuiButtonBase-root .MuiTouchRipple-root": {
          display: "none",
        },
      },
    } as SxProps<Theme>),
};

// =============================================================================
// SEARCH BOX COMPONENT STYLES
// =============================================================================

export const searchBoxStyles = {
  /** Search icon styling */
  searchIcon: {
    color: "text.secondary",
  } as SxProps<Theme>,

  /** Search icon styling when stalled */
  searchIconStalled: {
    color: "text.secondary",
    opacity: 0.5,
  } as SxProps<Theme>,

  /** Clear button styling for search box */
  clearButton: {
    color: "text.secondary",
    p: 0.5,
    "&:hover": {
      color: "text.primary",
    },
  } as SxProps<Theme>,

  /** Text field styling for search box */
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: "background.paper",
      px: "20px",
      "& input": {
        py: "12px",
        pl: 0,
        pr: "20px",
        textAlign: "left",
      },
      "&:hover fieldset": {
        borderColor: "primary.main",
      },
      "&.Mui-focused fieldset": {
        borderColor: "primary.main",
      },
    },
    "& .MuiInputBase-input::placeholder": {
      color: "text.secondary",
      opacity: 0.8,
      textAlign: "left",
    },
  } as SxProps<Theme>,

  /** Tooltip styling */
  tooltip: {
    "& .MuiTooltip-tooltip": {
      bgcolor: "white",
      color: theme.palette.primary.main,
      fontSize: "0.875rem",
    },
    "& .MuiTooltip-arrow": {
      color: "white",
    },
  } as SxProps<Theme>,
};

// =============================================================================
// RECIPE DIRECTORY PAGE STYLES
// =============================================================================

/**
 * Recipe Directory Page Styles
 *
 * Contains styling definitions specific to the RecipeDirectory page.
 * Extends userDirectoryStyles for common layout patterns.
 */
export const recipeDirectoryStyles = {
  /** Search header section */
  searchHeader: {
    px: "20px",
    pt: "20px",
    pb: "12px",
  } as SxProps<Theme>,

  /** Search box styling */
  searchBox: {
    width: "100%",
    "& .MuiInputBase-input": {
      fontSize: "1rem",
    },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.light,
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.light,
    },
  } as SxProps<Theme>,

  /** Results content grid for recipe cards */
  resultsContent: {
    px: "20px",
    pt: "12px",
    pb: "20px",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    "& .ais-Hits": {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
    },
    "& .ais-Hits-list.recipe-hits-grid": {
      display: "grid",
      gridTemplateColumns: "repeat(1, 1fr)",
      gap: "12px",
      listStyle: "none",
      margin: 0,
      padding: 0,
      flex: 1,
      alignContent: "start",
      [`@media (min-width: ${theme.breakpoints.values.sm}px)`]: {
        gridTemplateColumns: "repeat(2, 1fr)",
      },
      [`@media (min-width: ${theme.breakpoints.values.md}px)`]: {
        gridTemplateColumns: "repeat(3, 1fr)",
      },
      [`@media (min-width: ${theme.breakpoints.values.lg}px)`]: {
        gridTemplateColumns: "repeat(4, 1fr)",
      },
    },
    "& .recipe-hits-item": {
      listStyleType: "none",
    },
  } as SxProps<Theme>,
};
